import { Component, createRef } from 'react'
import { View } from 'react-native'
import styled from 'styled-components'
import { TextTrackList } from '@mwxltd/text-tracks'
import { OverrideScreenSize } from '../../../../style/OverrideScreenSize'
import { isIOS, isMobileDevice, mustUseHLS } from '../../../../style/browserDetect'
import { GroupWatchChatDock } from '../../../group-watch/components/GroupWatchChatDock'
import { getImageStream } from '../../PlayerControls/TrickPlayProgressBar/manifestParser/getImageStream.js'
import { isHLS } from '../../mimeTypes.js'
import { SeekTimeContext } from '../SeekTimeContext/SeekTimeContext'
import { updatedScrubDirectionState, updatedStateAfterScrubbing } from '../helpers/scrubbing'
import { playbackEvents } from '../playbackEvents'
import { PLAYBACK_RATES, SCRUB_INTERVAL, SCRUB_SPEEDS, SKIP_AMOUNT } from '../playbackRates'
import playerPropTypes from '../playerPropTypes'
import ReactPlayer from './ReactPlayer/ReactPlayer'
import SubtitlesRegion from './SubtitlesRegion/SubtitlesRegion'
import {
  addFullscreenListener,
  exitFullscreen,
  isFullscreenEnabled,
  removeFullscreenListener,
  requestFullscreen,
} from './helpers/fullscreen'

const Container = styled(View)`
  flex: 1;
  background: ${(props) => props.theme.colors.black};
  display: flex;
  flex-direction: row;
`

/* Forces subtitles to appear within player boundaries */
const PlayerWrapper = styled(View)`
  position: relative;
`

class WebPlayer extends Component {
  static defaultProps = {
    shownControlsDuration: 2000,
    fullscreenToggleSupported: !isIOS,
    userActiveOnKeydown: true,
    onPlaybackStatusChange: () => {},
    groupWatchChatEnabled: false,
    playbackRateChangeEnabled: true,
  }

  container = createRef()
  subtitles = createRef()

  tryAutoplayOnMount = true
  initialSeekTime = this.props.initialSeekTime

  state = {
    playing: false,
    scrubAmount: 0,
    scrubDirection: 1,
    playedSeconds: this.props.initialSeekTime || 0,
    duration: this.props.duration || 0,
    trickPlayStream: null,
    activeSubtitles: this.props.initialSubtitles,
    controlsShown: true,
    muted: this.props.initiallyMuted || false,
    volume: 1,
    fullscreen: this.props.parentFullscreen || false,
    fullChatVisible: true,
    playbackRate: 1,
  }

  async componentDidMount() {
    addFullscreenListener(this.toggleFullscreen)
    this.container.current.addEventListener('mousemove', this.setUserActive, true)
    this.container.current.addEventListener('mousedown', this.setUserActive, true)

    if (this.props.userActiveOnKeydown) {
      this.container.current.addEventListener('keydown', this.setUserActive, true)
    }

    if (this.props.fullscreenToggleSupported && isMobileDevice && !this.props.asChannelPlayer) {
      try {
        await this.handleFullscreenToggle()
      } catch (err) {
        // If the browser prevents going full screen programmatically the user will just see the
        // player normally and can go full screen manually, so the error can be ignored.
      }
    }
  }

  componentWillUnmount() {
    this.cleanUpTextTracks()
    clearTimeout(this.scrubTimeout)
    clearTimeout(this.userActiveTimeout)
    clearTimeout(this.preventHideControls)
    removeFullscreenListener(this.toggleFullscreen)
    this.container.current.removeEventListener('mousemove', this.setUserActive, true)
    this.container.current.removeEventListener('mousedown', this.setUserActive, true)

    if (this.props.userActiveOnKeydown) {
      this.container.current.removeEventListener('keydown', this.setUserActive, true)
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { playbackStatusOverride } = this.props

    if (playbackStatusOverride && prevProps.playbackStatusOverride !== playbackStatusOverride) {
      const { playing, seekTime } = playbackStatusOverride

      this.seekTo && this.seekTo(seekTime)

      this.setState({
        playing,
        playedSeconds: seekTime,
      })
    }

    if (!prevState.scrubAmount && this.state.scrubAmount) {
      this.scrubbingHandler()
      this.props.onScrubStarted()
    } else if (prevState.scrubAmount && !this.state.scrubAmount) {
      this.seekTo(this.state.playedSeconds)
      this.props.onScrubEnded(this.state.playedSeconds)
    }

    if (prevState.activeSubtitles !== this.state.activeSubtitles) {
      this.updateActiveSubtitles(this.state.activeSubtitles)
    }

    if (prevProps.subtitles !== this.props.subtitles) {
      this.cleanUpTextTracks()
      this.initTextTracks()
    }

    if (prevProps.duration !== this.props.duration && this.props.duration) {
      this.setState({
        duration: Math.floor(this.props.duration),
        playedSeconds: this.props.initialSeekTime ?? 0,
      })
    }

    if (prevProps.url !== this.props.url && this.props.initialSeekTime) {
      // onReady the player will seek to this time
      this.initialSeekTime = this.props.initialSeekTime
    }

    if (this.state.playing && !prevState.playing) {
      // In some cases the player state remains paused (Edge)
      this.getInternalPlayer()?.play()
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { state, props } = this
    const { creditsStartSeconds: creditsStart } = nextProps
    return (
      // The video has changed
      props.url !== nextProps.url ||
      // The player resized
      props.width !== nextProps.width ||
      props.height !== nextProps.height ||
      // The requested play/pause state has changed
      state.playing !== nextState.playing ||
      // The end of the title was reached
      (creditsStart &&
        state.playedSeconds <= creditsStart &&
        creditsStart < nextState.playedSeconds) ||
      // The control bar is open, so progress seconds are updating;
      // user interaction may also change a number of states
      state.controlsShown ||
      nextState.controlsShown ||
      // Propagate changes in the advert
      props.advert !== nextProps.advert ||
      // Playback status has changed externally
      props.playbackStatusOverride !== nextProps.playbackStatusOverride ||
      props.videoHidden !== nextProps.videoHidden
    )
  }

  initTextTracks() {
    const tracks = this.getFormattedTracks()
    if (tracks.length && this.player) {
      this.trackList = TextTrackList.fromTrackOptions(
        tracks,
        this.getInternalPlayer().getVideoElement(),
      )
      this.trackList.addEventListener('trackloaderror', this.onTrackLoadError)
      this.updateActiveSubtitles(this.state.activeSubtitles)
    }
  }

  cleanUpTextTracks() {
    if (this.trackList) {
      this.trackList.removeEventListener('trackloaderror', this.onTrackLoadError)
      this.trackList.removeInternalListeners()
      this.trackList = null
    }
  }

  onTrackLoadError = (ev) => this.props.onError(ev)

  setUserActive = () => {
    clearTimeout(this.userActiveTimeout)

    // Touches immediately after showing the controls may inadvertently close it again
    if (!this.state.controlsShown) {
      this.preventHideControls = true
      clearTimeout(this.preventHideTimeout)
      this.preventHideTimeout = setTimeout(() => {
        this.preventHideControls = false
      }, 500)
    }

    this.setState((state) => (state.controlsShown ? null : { controlsShown: true }))
    this.userActiveTimeout = setTimeout(this.hideControls, this.props.shownControlsDuration)
  }

  hideControls = () => {
    if (this.preventHideControls) return
    // Wait a few ticks so the `setUserActive` that might have triggered can pass
    // iPad in particular requires more than one tick
    setTimeout(() => {
      clearTimeout(this.userActiveTimeout)
      this.setState((state) => (state.playing ? { controlsShown: false } : null))
    }, 100)
  }

  // To avoid playing the previous position while the new video segment loads,
  // the player is paused immediately (bypassing React render) and restarted
  // after seeking if it was playing in the first place.
  seekTo(seconds) {
    const player = this.getInternalPlayer()
    player?.pause()
    this.player?.seekTo(seconds, 'seconds')
    if (this.state.playing && !this.props.videoHidden) {
      player?.play()
    }
  }

  seekIfInitialTimeIsSet = () => {
    if (this.initialSeekTime) {
      this.seekTo(this.initialSeekTime)
      this.initialSeekTime = false
    }
  }

  // The player is initially paused. Playback needs to start to properly load the HLS
  // manifest and be ready to for an initial seek, and we also need to check whether
  // autoplay is allowed in the current context. Both is achieved by starting the
  // player and observing the result of the returned promise.
  autoplayOnMount() {
    if (this.props.videoHidden) return
    this.getInternalPlayer()
      .play()
      .then(() => {
        // Seeking is only possible once the video has started playing for the first time.
        this.seekIfInitialTimeIsSet()
        // Also set the fact that playback succeeded on the state
        this.setState({ playing: true })
      })
      .catch(() => {
        // Playback usually fails because of autoplay without DOM interaction after a
        // refresh or on page load on mobile. The initialSeekTime is maintained in these
        // cases and will be applied the next time playback is started when the user
        // clicks the play button. Controls are shown to make this obvious.
        this.setState({ controlsShown: true })
      })
  }

  forwardCb(prop, ...args) {
    this.props[prop]?.(...args)
  }

  onReady = (player) => {
    this.player = player

    if (isHLS(this.props.mime)) {
      getImageStream(this.props.url)
        .then((trickPlayStream) => this.setState({ trickPlayStream }))
        .catch(this.props.notifyError, this.props)
    }

    if (this.tryAutoplayOnMount) {
      this.tryAutoplayOnMount = false
      this.autoplayOnMount()
    } else {
      // Set the current seek time as early as possible for the progress bar UI
      if (this.initialSeekTime) {
        this.setState({ playedSeconds: this.initialSeekTime })
      }
      // When the url changes there may be an initial seek time again
      this.seekIfInitialTimeIsSet()
    }
    this.forwardCb('onReady')
  }

  onStart = () => {
    // This is the earliest this.initTextTracks() can be called on iPhone/iPad
    // for tracks not to get stuck in the on state.
    this.initTextTracks()
    this.forwardCb('onStart')
  }

  onRequestPlayPause = () => (this.state.playing ? this.onRequestPause() : this.onRequestPlay())

  onRequestPlay = () => {
    this.setState({ playing: true, scrubAmount: 0 })
    this.props.onPlaybackStatusChange({
      playing: true,
      seekTime: this.state.playedSeconds,
      type: playbackEvents.PLAY,
    })
    this.props.onPlayed?.(this.state.playedSeconds)
  }

  onRequestPause = () => {
    this.setState({ playing: false, scrubAmount: 0 })
    this.props.onPlaybackStatusChange({
      playing: false,
      seekTime: this.state.playedSeconds,
      type: playbackEvents.PAUSE,
    })
    this.props.onPaused?.()
  }

  onRequestRestart = () => this.seekTo(0)

  onRewind = () => this.setState(updatedScrubDirectionState(SCRUB_SPEEDS, -1))

  onFastForward = () => this.setState(updatedScrubDirectionState(SCRUB_SPEEDS, 1))

  scrubbingHandler = () => {
    this.setState((state) => {
      const newState = updatedStateAfterScrubbing(SCRUB_INTERVAL, SCRUB_SPEEDS)(state)
      if (newState && newState.scrubAmount !== 0) {
        this.scrubTimeout = setTimeout(this.scrubbingHandler, SCRUB_INTERVAL)
      }
      return newState
    })
  }

  handleSkipBack = () => {
    this.handleSkipToSeekTime(Math.max(0, this.state.playedSeconds - SKIP_AMOUNT))
  }

  handleSkipForward = () => {
    const { playedSeconds, duration } = this.state
    this.handleSkipToSeekTime(Math.min(duration, playedSeconds + SKIP_AMOUNT))
  }

  handleSkipToSeekTime = (seekTime) => {
    const { playing } = this.state

    this.seekTo(Math.floor(seekTime))
    this.setState({ playedSeconds: seekTime })
    this.props.onPlaybackStatusChange({
      playing,
      seekTime,
      type: playing ? playbackEvents.SKIP_PLAY : playbackEvents.SKIP_PAUSE,
    })
    this.props.onSkipped(seekTime)
  }

  handleScrubbingStart = () => {
    this.scrubbing = true
    this.props.onScrubStarted()
  }

  handleScrubbingEnd = (seekTime) => {
    this.scrubbing = false
    this.seekTo(seekTime)
    this.setState({ playedSeconds: seekTime }) // Set immediately to avoid glitches

    const { playing } = this.state

    this.props.onPlaybackStatusChange({
      playing,
      seekTime,
      type: playing ? playbackEvents.SKIP_PLAY : playbackEvents.SKIP_PAUSE,
    })
    this.props.onScrubEnded(seekTime)
  }

  handleVolumeChange = (volume) => this.setState({ volume })

  handleMuteToggle = () => {
    this.props.onMuteToggle?.(!this.state.muted)
    this.setState((state) => ({ muted: !state.muted }))
  }

  handleFullscreenToggle = () =>
    this.state.fullscreen
      ? this.handleExitFullscreen()
      : requestFullscreen(
          this.props.adPlayerContainer ? this.props.adPlayerContainer : this.container.current,
        )

  toggleFullscreen = () => this.setState({ fullscreen: isFullscreenEnabled() })

  handleExitFullscreen = () => {
    exitFullscreen()
    this.showChat()
  }

  toggleFullChat = () => (this.state.fullChatVisible ? this.hideChat() : this.showChat())
  hideChat = () => this.setState({ fullChatVisible: false })
  showChat = () => this.setState({ fullChatVisible: true })

  setPlaybackRate = (playbackRate) => {
    this.setState({ playbackRate })
  }

  /** @param {{ played, playedSeconds, loaded, loadedSeconds }} progress Fraction and actual seconds values */
  onProgress = ({ playedSeconds }) => {
    if (!this.initialSeekTime && !this.scrubbing && !this.state.scrubAmount) {
      this.setState({ playedSeconds: Math.floor(playedSeconds) })
      this.forwardCb('onProgress', playedSeconds)
    }
  }

  onDuration = (duration) => {
    if (Number.isFinite(duration)) {
      duration = Math.floor(duration)
      this.setState({ duration })
      this.forwardCb('onDuration', duration)
    }
  }

  onError = (plainOrShakaError, ...args) => {
    const error = plainOrShakaError instanceof Error ? plainOrShakaError : new Error('Player error')
    const shakaError = plainOrShakaError === error ? undefined : plainOrShakaError

    if (error.code === 0) {
      // video cannot autoplay. This happens a lot on Safari from v14
      this.onRequestPause()
      return
    }

    if (error.code === 20) {
      // Abort error triggered when a player event like 'play' gets interrupted,
      // e.g. if onRequestPause is called immediately on mount. Can usually be ignored.
      return
    }

    this.forwardCb('onError', error, shakaError, ...args)
  }

  onSubtitlesChosen = (language = null) => this.setState({ activeSubtitles: language })

  updateActiveSubtitles(language) {
    if (this.trackList) {
      for (let i = 0; i < this.trackList.length; ++i) {
        const track = this.trackList[i]
        if (track.kind === 'subtitles') {
          track.mode = track.language === language ? 'showing' : 'disabled'
          track.oncuechange = track.language === language ? this.onCueChange : null
        }
      }
    }
  }

  onCueChange = (ev) => {
    const { activeCues } = ev.target
    activeCues && this.subtitles.current?.updateCue(activeCues[0])
  }

  getFormattedTracks() {
    return (this.props.subtitles || []).map((track) => ({
      kind: 'subtitles',
      src: track.url,
      label: track.languageName,
      language: track.language,
    }))
  }

  getInternalPlayer() {
    return this.player?.getInternalPlayer()
  }

  getFullScreenElement = () => this.container.current

  render() {
    const {
      url,
      mime,
      dash,
      width = '100%',
      height = '100%',
      shownControlsHeight = 60,
      fullscreenToggleSupported,
      playsInline = true,
      onRequestClose,
      subtitles,
      actors,
      title,
      type,
      children,
      creditsStartSeconds,
      nextEpisode,
      advert,
      onRequestCloseAdvert,
      groupWatchChatEnabled,
      chatWidth = 0,
      chatPosition = 'right', // alternatively 'bottom'
      playbackRateChangeEnabled,
      videoHidden,
      pauseOverlayLeft,
      pauseOverlayRight,
      filmSlug,
      festivalId,
      asChannelPlayer,
      isMobile,
      channelPlayerFullScreenWidth,
      ...props
    } = this.props
    const {
      playing,
      playedSeconds,
      duration,
      trickPlayStream,
      controlsShown,
      activeSubtitles,
      muted,
      volume,
      fullscreen,
      fullChatVisible,
      playbackRate,
      scrubAmount,
    } = this.state

    const creditsReached = playedSeconds > creditsStartSeconds
    const chatShown = groupWatchChatEnabled && fullChatVisible

    const videoHeight = videoHidden
      ? 0
      : !chatShown || chatPosition === 'right'
      ? height
      : height * 0.33

    const videoWidth = videoHidden
      ? 0
      : !chatShown || chatPosition === 'bottom'
      ? width
      : width - chatWidth

    const channelPlayerFSWidth =
      !isMobile && asChannelPlayer && fullscreen ? channelPlayerFullScreenWidth : videoWidth

    // We support full screen in a channel's player
    // regardless of the device since the page contains
    // the player by default in its structure and not in
    // the fullscreen state initially.
    const isFullscreenSupported = asChannelPlayer || fullscreenToggleSupported
    return (
      <Container
        ref={this.container}
        style={{
          height,
          width,
          flexDirection: chatPosition === 'right' ? 'row' : 'column',
        }}
      >
        <OverrideScreenSize height={videoHeight} width={channelPlayerFSWidth}>
          <PlayerWrapper>
            <ReactPlayer
              {...props}
              url={!dash || mustUseHLS ? url : dash.url}
              mime={!dash || mustUseHLS ? mime : dash.mime}
              width={channelPlayerFSWidth}
              height={videoHeight}
              playing={playing && !videoHidden}
              playbackRate={playbackRate}
              playsInline={playsInline}
              onReady={this.onReady}
              onStart={this.onStart}
              onError={this.onError}
              onProgress={this.onProgress}
              onDuration={this.onDuration}
              progressInterval={1000}
              muted={muted}
              volume={volume}
              asChannelPlayer={asChannelPlayer}
            />
            <SubtitlesRegion
              ref={this.subtitles}
              playerWidth={channelPlayerFSWidth}
              playerHeight={videoHeight}
              enabled={!!activeSubtitles && !videoHidden}
              yOffset={() => (controlsShown && shownControlsHeight) || null}
            />
            <SeekTimeContext.Provider
              value={{ seekTime: playedSeconds, totalTime: duration, trickPlayStream }}
            >
              {/* Video Controls */}
              {children({
                playerWidth: channelPlayerFSWidth,
                playerHeight: videoHeight,
                onRequestClose: onRequestClose,

                playing,
                scrubbing: scrubAmount !== 0,
                onPlay: this.onRequestPlay,
                onPause: this.onRequestPause,
                onPlayPause: this.onRequestPlayPause,
                onRestart: this.onRequestRestart,
                onRewind: this.onRewind,
                onFastForward: this.onFastForward,

                onSkipBack: this.handleSkipBack,
                onSkipForward: this.handleSkipForward,
                onScrubbingStart: this.handleScrubbingStart,
                onScrubbingEnd: this.handleScrubbingEnd,

                muted,
                onToggleMute: this.handleMuteToggle,
                volume,
                onVolumeChange: this.handleVolumeChange,

                controlsShown: controlsShown && !videoHidden,
                onUserActive: this.setUserActive,
                onRequestHideControls: this.hideControls,
                fullscreen,
                onFullscreenPress: isFullscreenSupported && this.handleFullscreenToggle,
                getFullScreenElement: this.getFullScreenElement,

                creditsReached,
                nextEpisode,

                subtitles,
                activeSubtitles,
                onSubtitlesChosen: this.onSubtitlesChosen,

                actors,
                title,
                type,

                advert,
                onRequestCloseAdvert,

                fullChatVisible,
                toggleFullChat: groupWatchChatEnabled && !!chatWidth && this.toggleFullChat,

                playbackRate,
                setPlaybackRate: playbackRateChangeEnabled && this.setPlaybackRate,
                playbackRateOptions: PLAYBACK_RATES,

                pauseOverlayLeft,
                pauseOverlayRight,

                filmSlug,
                festivalId,
              })}
            </SeekTimeContext.Provider>
          </PlayerWrapper>
        </OverrideScreenSize>

        {groupWatchChatEnabled && (
          <GroupWatchChatDock
            fullChatVisible={fullChatVisible && !!chatWidth}
            chatPosition={chatPosition}
          />
        )}
      </Container>
    )
  }
}

WebPlayer.propTypes = playerPropTypes

export default WebPlayer
